import { TITLES as T } from './routes';
import { NavCategory, NavNode } from './types';

const lucide = (name: string) => ({ pack: 'lucide' as const, name });
const product = (name: string) => ({ pack: 'product' as const, name });

const node = (id: string, title: string, icon: string, group?: string, children?: NavNode[]): NavNode => ({
  id, title, icon: lucide(icon), group, children,
});

/** Panel section labels. Order here is the order sections render in. */
export const GROUPS = {
  overview: 'Overview',
  referrals: 'Referrals',
  census: 'Census',
  reports: 'Referral Analytics',
  complyReports: 'Reports',
} as const;

export const GROUP_ORDER: readonly string[] = [
  GROUPS.overview, GROUPS.referrals, GROUPS.census, GROUPS.reports, GROUPS.complyReports,
];

/** Preset titles the panel deliberately has no node for: avatar-menu actions. */
export const NON_PANEL_TITLES: readonly string[] = [T.profile, T.logOut];

/** Child nodes that inherit a parent's visibility instead of carrying a preset entry of their own. */
export const INHERITED_CHILD_TITLES: readonly string[] = [T.dailyTrendReport];

/**
 * Product grouping is a draft pending product sign-off — remapping a screen is a data edit here,
 * never a refactor. Titles must match PRESET_TITLES entries or the node can never resolve.
 *
 * `title` is task language (what users navigate by); `productLabel` carries the brand name and
 * renders as the caption under the rail icon.
 */
export const NAV_CONFIG: NavCategory[] = [
  {
    product: 'gateway',
    title: 'Referrals',
    productLabel: 'GatewAI',
    // Rail icons are lucide placeholders until the designer ships the 3 product SVGs.
    icon: product('gateway'),
    screens: [
      node('manager-dashboard', T.managerDashboard, 'LayoutDashboard', GROUPS.overview),
      { ...node('kpi-dashboard', T.kpiDashboard, 'BarChart3', GROUPS.overview), badge: 'NEW' },
      { ...node('corporate-overview', T.corporateOverview, 'Globe'), rail: true },
      node('live-bed-board', T.liveBedBoard, 'BedDouble', GROUPS.overview),
      node('discharge-plan', T.dischargePlan, 'CalendarCheck', GROUPS.overview),

      node('referral-inbox', T.referralInbox, 'Inbox', GROUPS.referrals),
      node('create-referral', T.createReferral, 'FilePlus2', GROUPS.referrals),
      node('second-approval', T.secondApproval, 'CheckCheck', GROUPS.referrals),

      node('live-census', T.liveCensus, 'Users', GROUPS.census),
      node('census-trends', T.censusTrends, 'TrendingUp', GROUPS.census, [
        node('daily-trend-report', T.dailyTrendReport, 'CalendarClock'),
      ]),
      node('adt-report', T.adtReport, 'ArrowLeftRight', GROUPS.census),
      { ...node('kpis', T.kpis, 'Target', GROUPS.census), badge: 'NEW' },
      node('average-los-report', T.averageLosReport, 'Timer', GROUPS.census),
      node('hospital-tracker', T.hospitalTracker, 'Building2', GROUPS.census),

      node('referrals-summary', T.referralsSummary, 'ClipboardList', GROUPS.reports),
      node('referral-conversions', T.referralConversions, 'PieChart', GROUPS.reports),
      { ...node('referral-response-report', T.referralResponseReport, 'MessageSquare', GROUPS.reports), label: 'Referral Response Time' },
      node('returning-referrals', T.returningReferrals, 'Undo2', GROUPS.reports),
      { ...node('decline-insights', T.declineInsights, 'BarChart3', GROUPS.reports), label: 'Decline Reason Analysis' },
    ],
  },
  {
    product: 'comply',
    title: 'Compliance',
    productLabel: 'ComplAI',
    icon: product('comply'),
    productFlag: 'comply',
    screens: [
      node('complai-dashboard', T.complaiDashboard, 'Gauge', GROUPS.overview),
      // Same screen as the Gateway entry; the rail highlights Gateway when reached by url.
      { ...node('comply-bed-board', T.liveBedBoard, 'BedDouble', GROUPS.overview), mirror: true },

      node('complai-reports', T.complaiReports, 'FileText', GROUPS.complyReports),
      node('assessment-report', T.assessmentReport, 'Stethoscope', GROUPS.complyReports),
      node('event-reports', T.eventReports, 'FileSpreadsheet', GROUPS.complyReports),
    ],
  },
  {
    product: 'mds',
    title: 'MDS',
    productLabel: 'MDS',
    icon: product('mds'),
    productFlag: 'mds',
    screens: [
      // Same dashboard and Report Builder as ComplAI, showing the MDS signals and reports.
      node('mds-dashboard', T.mdsDashboard, 'Gauge', GROUPS.overview),

      { ...node('triple-check-report', T.tripleCheckReport, 'ListChecks', GROUPS.complyReports), badge: 'BETA' },
      node('mds-payer-change-report', T.payerChangeReport, 'Wallet', GROUPS.complyReports),
      node('mds-reports', T.mdsReports, 'FileText', GROUPS.complyReports),
    ],
  },
  {
    product: 'risk',
    title: 'High Risk',
    productLabel: 'RiskAI',
    icon: product('risk'),
    screens: [
      { ...node('risk-bed-board', T.liveBedBoard, 'BedDouble', GROUPS.overview), mirror: true },
      node('risk-high-risk', T.highRiskPatients, 'TriangleAlert', GROUPS.overview),
    ],
  },
  {
    // Not a product: Users sits in the avatar menu, Admin renders as a footer tile above the avatar.
    product: 'general',
    title: 'Account & Admin',
    placement: 'settings',
    icon: product('general'),
    screens: [
      node('users', T.users, 'Users'),
      node('admin', T.admin, 'UserCog'),
    ],
  },
];
